import { Injectable, Inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { loadPrinter, loadPrinterSuccess, loadPrinterFailure, connectPrinter, connectPrinterSuccess, connectPrinterFailure, ignorePrinter, resetPrinterIgnore } from './printer.actions';
import { of, from } from 'rxjs';
import { catchError, switchMap, map, tap } from 'rxjs/operators';
import { PrinterService } from '../../shared/services/printer.service';
import { DialogService } from 'primeng/dynamicdialog';
import { PrinterConfigModalComponent } from '../../shared/components/printer-config-modal/printer-config-modal.component';

@Injectable()
export class PrinterEffects {

  loadPrinter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPrinter),
      switchMap(() => {
        const printerName = localStorage.getItem('printerDevice');
        const printerIgnored = localStorage.getItem('printerIgnored');

        if (printerIgnored === 'true') {
          console.log('Printer was previously ignored, restoring that state');
          return of(ignorePrinter());
        }

        if (!printerName) {
          console.log('No printer configuration found in localStorage (effects)');
          return of(loadPrinterFailure({ error: 'No printer configuration found' }));
        }

        console.log('Printer configuration found in localStorage:', printerName);

        return from(this.printerService.connectToStoredDevice()).pipe(
          map(connected => {
            if (connected) {
              console.log('Conexión exitosa a la impresora almacenada');
              return { type: '[Printer] Load Printer Complete' };
            } else {
              console.log('No se pudo conectar a la impresora almacenada automáticamente (restricciones WebUSB)');
              return { type: '[Printer] Auto Connect Failed But Config Exists' };
            }
          }),
          catchError(error => {
            console.error('Error al intentar conectar con impresora guardada:', error);
            return of({ type: '[Printer] Auto Connect Failed But Config Exists' });
          })
        );
      })
    )
  );

  connectPrinter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(connectPrinter),
      switchMap(() => {
        return from(this.printerService.requestDevice()).pipe(
          map(connected => {
            if (connected) {
              return { type: '[Printer] Connect Printer Complete' }; // Acción nula
            } else {
              return connectPrinterFailure({ error: 'Failed to connect to printer' });
            }
          }),
          catchError(error => of(connectPrinterFailure({ error })))
        );
      })
    )
  );

  showConfigModal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPrinterFailure),
      tap((action) => {
        const printerName = localStorage.getItem('printerDevice');
        const printerIgnored = localStorage.getItem('printerIgnored');

        if (printerIgnored === 'true') {
          console.log('PrinterEffects: No mostrando modal automáticamente porque la impresora está ignorada');
          return;
        }

        if (!printerName) {
          console.log('PrinterEffects: Abriendo modal de configuración automáticamente (sin configuración previa)');
          this.showPrinterConfigModal();
          return;
        }

        if (action.error && (
            action.error.toString().includes('Requiere reautorización') ||
            action.error === 'Dispositivo guardado no encontrado'
          )) {
          console.log('PrinterEffects: Detectado error de reconexión por restricciones de seguridad WebUSB');
          console.log('PrinterEffects: Abriendo modal para permitir la reautorización del dispositivo');
          this.showPrinterConfigModal();
          return;
        }

        console.log('PrinterEffects: No mostrando modal para otros tipos de error:', action.error);
      }),
      map(() => ({ type: '[Printer] Show Config Modal' }))
    )
  );

  private showPrinterConfigModal(): void {
    setTimeout(() => {
      this.dialogService.open(PrinterConfigModalComponent, {
        header: 'Configurar Impresora',
        width: '70%',
        contentStyle: { overflow: 'auto' },
        baseZIndex: 10000,
        maximizable: false,
        closable: false,
        dismissableMask: false
      });
    }, 500);
  }

  constructor(
    private actions$: Actions,
    private printerService: PrinterService,
    private dialogService: DialogService
  ) {}
}
